import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Gift, LogOut, ShieldCheck, UserCog, TriangleAlert, Clock, LifeBuoy, MailCheck, LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useEditor } from '../../context/EditorContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ProfileDialog } from './ProfileDialog';
import { BillingDialog } from '../billing/BillingDialog';
import { EmailTestDialog } from './EmailTestDialog';
import { avatarColorCss, avatarSrc, initialsOf } from '../../lib/avatar';
import { subscriptionSummary } from '../../lib/billingApi';

/**
 * Who is signed in (ribbon quick access): avatar → name, email, how they sign in, the subscription in one line,
 * Profile settings…, Billing & plan…, Refer a friend…, Help Center, Sign out — plus, for owner accounts only
 * (`user.is_owner`, OWNER_EMAILS on the server), "Send me a test email…" to confirm email delivery after a deploy.
 * A trial or a payment problem also shows as a small pill next to the avatar (wide screens) that opens Billing & plan.
 * Signing out with unsaved work asks first; then the server session is ended and this browser forgets the account's
 * open tabs and recents (AuthContext.signOut) before returning to the landing page.
 */
export const AccountMenu = () => {
  const { user, signOut } = useAuth();
  const { state } = useEditor();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingTab, setBillingTab] = useState('plan');
  const [emailTestOpen, setEmailTestOpen] = useState(false);

  // Deep link from the billing emails: /app?billing=1 opens Billing & plan, /app?billing=refer opens Refer a friend.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const want = params.get('billing');
    if (!want) return;
    setBillingTab(want === 'refer' ? 'refer' : 'plan');
    setBillingOpen(true);
    params.delete('billing');
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, []);
  if (!user) return null;

  const unsaved = (state.docTabs || []).some((t) => (t.id === state.activeTabId ? state.docRevision !== state.savedRevision : t.doc && t.doc.docRevision !== t.doc.savedRevision));
  const providers = (user.providers || []).map((p) => (p === 'google' ? 'Google' : 'email + password')).join(' · ');
  const src = avatarSrc(user);
  const accent = avatarColorCss(user.avatar_color);
  const summary = subscriptionSummary(user.billing);
  const pill = summary.tone === 'trial' ? { text: summary.title, className: 'bg-primary/10 text-primary hover:bg-primary/15', Icon: Clock }
    : summary.tone === 'warn' && user.billing?.status === 'past_due' ? { text: 'Payment problem', className: 'bg-[hsl(var(--wp-context-amber))]/15 text-[hsl(var(--wp-context-amber))] hover:bg-[hsl(var(--wp-context-amber))]/25', Icon: TriangleAlert }
      : null;

  const openBilling = (tab) => { setBillingTab(tab); setBillingOpen(true); };

  const doSignOut = async () => {
    if (busy) return;
    if (unsaved && !window.confirm('You have unsaved changes. Sign out anyway? Unsaved work will be lost.')) return;
    setBusy(true);
    try {
      await signOut();
      toast.success('Signed out', { description: 'Your publications stay safe in your account.', id: 'auth' });
      navigate('/', { replace: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {pill && (
        <button type="button" onClick={() => openBilling('plan')} className={`hidden lg:inline-flex items-center gap-1 h-6 rounded-full px-2 mr-1 text-[11px] font-medium whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors duration-150 ${pill.className}`} title={summary.detail} data-testid="account-billing-pill">
          <pill.Icon className="h-3 w-3" aria-hidden="true" />{pill.text}
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="h-7 w-7 ml-0.5 rounded-full ring-1 ring-border hover:ring-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-shadow duration-150"
            title={`${user.name || user.email} — account`}
            aria-label="Account menu"
            data-testid="account-menu-trigger"
          >
            <Avatar className="h-7 w-7">
              {src && <AvatarImage src={src} alt="" referrerPolicy="no-referrer" />}
              <AvatarFallback className={`text-[10px] font-semibold ${accent ? 'text-white' : 'bg-primary/15 text-primary'}`} style={accent ? { backgroundColor: accent } : undefined} data-testid="account-menu-initials">{initialsOf(user)}</AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72" data-testid="account-menu">
          <DropdownMenuLabel className="font-normal">
            <div className="text-sm font-semibold text-foreground truncate" data-testid="account-menu-name">{user.name || user.email}</div>
            <div className="text-xs text-muted-foreground truncate" data-testid="account-menu-email">{user.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5 text-[11px] text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden="true" />Signs in with {providers || 'email + password'}</div>
          {summary.tone !== 'none' && (
            <div className={`px-2 py-1.5 text-[11px] flex items-start gap-1.5 ${summary.tone === 'warn' ? 'text-[hsl(var(--wp-context-amber))]' : summary.tone === 'off' ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="account-menu-billing-line">
              <CreditCard className={`h-3.5 w-3.5 shrink-0 mt-px ${summary.tone === 'warn' || summary.tone === 'off' ? '' : 'text-primary'}`} aria-hidden="true" /><span className="min-w-0">{summary.title}</span>
            </div>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setProfileOpen(true)} className="text-sm" data-testid="account-menu-profile">
            <UserCog className="h-4 w-4 mr-2" aria-hidden="true" />Profile settings…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openBilling('plan')} className="text-sm" data-testid="account-menu-billing">
            <CreditCard className="h-4 w-4 mr-2" aria-hidden="true" />Billing &amp; plan…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openBilling('refer')} className="text-sm" data-testid="account-menu-refer">
            <Gift className="h-4 w-4 mr-2" aria-hidden="true" />Refer a friend…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => window.open('/help', '_blank', 'noopener')} className="text-sm" data-testid="account-menu-help">
            <LifeBuoy className="h-4 w-4 mr-2" aria-hidden="true" />Help Center
          </DropdownMenuItem>
          {user.is_owner && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid="account-menu-owner-label">Owner tools</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => navigate('/owner')} className="text-sm" data-testid="account-menu-owner-dashboard">
                <LayoutDashboard className="h-4 w-4 mr-2" aria-hidden="true" />Owner dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setEmailTestOpen(true)} className="text-sm" data-testid="account-menu-email-test">
                <MailCheck className="h-4 w-4 mr-2" aria-hidden="true" />Send me a test email…
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); doSignOut(); }} disabled={busy} className="text-sm" data-testid="account-menu-sign-out">
            <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />{busy ? 'Signing out…' : 'Sign out'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
      <BillingDialog open={billingOpen} onOpenChange={setBillingOpen} tab={billingTab} onTabChange={setBillingTab} />
      {user.is_owner && <EmailTestDialog open={emailTestOpen} onOpenChange={setEmailTestOpen} />}
    </>
  );
};
